/**
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — finance cheques view
 * canonical pattern: V2Button, V2EmptyState, LoadingState, StatePanel
 */
import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { isBusinessDate, systemClock, type BusinessDate } from '@sakhtar/calendar';
import { V2Button, V2EmptyState, LoadingState, StatePanel } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { ChequeCameraScanner } from './ChequeCameraScanner.js';
import { ChequeField, ChequeModal } from './ChequeModal.js';
import {
  allowedChequeTransitions,
  isPositiveRial,
  isValidSayadId,
  normalizeDigits,
  pickDefaultPeriod,
  transitionNeedsGlAccount,
  TRANSITION_BUTTON_LABEL,
} from './cheque-actions.js';
import type {
  AccountListItem,
  AccountListResponse,
  BankAccountDetail,
  BankAccountListResponse,
  ChequeDirection,
  ChequeItem,
  ChequeListResponse,
  ChequeScanResult,
  ChequeStatus,
  ChequebookItem,
  ChequebookListResponse,
  FiscalPeriod,
  FiscalPeriodListResponse,
  TreasuryAccount,
  TreasuryAccountListResponse,
} from '../../lib/types/finance.js';
import { CHEQUE_STATUS_LABEL } from '../../lib/types/finance.js';
import './cheques.css';

type Tab = ChequeDirection | 'chequebooks';
type Form = Record<string, string>;

const today = (): string => systemClock.today();

const inwardInitial = (): Form => ({
  sayadId: '',
  serialNumber: '',
  series: '',
  bankName: '',
  branchName: '',
  branchCode: '',
  accountNumber: '',
  iban: '',
  amountRial: '',
  amountWords: '',
  issueDate: today(),
  dueDate: today(),
  drawerName: '',
  drawerNationalId: '',
  payeeName: '',
  payeeNationalId: '',
  treasuryAccountId: '',
  counterpartAccountId: '',
  chequeAccountId: '',
  notes: '',
});

const outwardInitial = (): Form => ({
  sayadId: '',
  serialNumber: '',
  series: '',
  bankAccountId: '',
  chequebookId: '',
  bankName: '',
  branchName: '',
  amountRial: '',
  issueDate: today(),
  dueDate: today(),
  payeeName: '',
  payeeNationalId: '',
  counterpartAccountId: '',
  chequePayableAccountId: '',
  notes: '',
});

const chequebookInitial = (): Form => ({
  bankAccountId: '',
  series: '',
  fromSerial: '',
  toSerial: '',
});

const transitionInitial = (): Form => ({
  transitionDate: today(),
  targetTreasuryAccountId: '',
  targetGlAccountId: '',
  notes: '',
});

function useTenantGet<T>(
  slug: string | undefined,
  base: string,
  suffix: string,
  deps: readonly unknown[],
) {
  return useApiGet<T>(slug ? `${base}/api/v1/${suffix}` : null, deps);
}

export function ChequesPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const apiBase = `${base}/api/v1/finance/treasury/cheques`;
  const { unit } = useAmountDisplay();

  const [activeTab, setActiveTab] = useState<Tab>('inward');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [modal, setModal] = useState<'inward' | 'outward' | 'chequebook' | null>(null);
  const [transition, setTransition] = useState<{
    cheque: ChequeItem;
    to: ChequeStatus;
  } | null>(null);

  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiFilled, setAiFilled] = useState<Record<string, boolean>>({});
  const [scanImage, setScanImage] = useState('');
  const [inward, setInward] = useState<Form>(inwardInitial);
  const [outward, setOutward] = useState<Form>(outwardInitial);
  const [chequebook, setChequebook] = useState<Form>(chequebookInitial);
  const [transitionForm, setTransitionForm] = useState<Form>(transitionInitial);
  // One key per opened form: a double click / retry replays the same request instead of duplicating.
  const idemKey = useRef<string>('');

  const chequesQuery = useTenantGet<ChequeListResponse>(
    slug,
    base,
    `finance/treasury/cheques${activeTab !== 'chequebooks' ? `?direction=${activeTab}` : ''}`,
    [slug, activeTab, refreshKey],
  );
  const chequebooksQuery = useTenantGet<ChequebookListResponse>(
    slug,
    base,
    'finance/treasury/cheques/chequebooks/list',
    [slug, refreshKey],
  );
  const treasuryQuery = useTenantGet<TreasuryAccountListResponse>(
    slug,
    base,
    'finance/treasury/accounts',
    [slug],
  );
  const bankQuery = useTenantGet<BankAccountListResponse>(
    slug,
    base,
    'finance/treasury/bank-accounts',
    [slug],
  );
  const periodsQuery = useTenantGet<FiscalPeriodListResponse>(
    slug,
    base,
    'accounting/fiscal-periods',
    [slug],
  );
  const glQuery = useTenantGet<AccountListResponse>(slug, base, 'finance/accounts', [slug]);

  const cheques = useMemo<readonly ChequeItem[]>(
    () => (chequesQuery.status === 'ready' ? chequesQuery.data.items : []),
    [chequesQuery],
  );
  const chequebooks = useMemo<readonly ChequebookItem[]>(
    () => (chequebooksQuery.status === 'ready' ? chequebooksQuery.data.items : []),
    [chequebooksQuery],
  );
  const treasuryAccounts = useMemo<readonly TreasuryAccount[]>(
    () => (treasuryQuery.status === 'ready' ? treasuryQuery.data.items : []),
    [treasuryQuery],
  );
  const bankAccounts = useMemo<readonly BankAccountDetail[]>(
    () => (bankQuery.status === 'ready' ? bankQuery.data.items : []),
    [bankQuery],
  );
  const periods = useMemo<readonly FiscalPeriod[]>(
    () => (periodsQuery.status === 'ready' ? periodsQuery.data.items : []),
    [periodsQuery],
  );
  const glAccounts = useMemo<readonly AccountListItem[]>(
    () =>
      glQuery.status === 'ready'
        ? glQuery.data.items.filter((a) => a.isPostable && a.status === 'active')
        : [],
    [glQuery],
  );
  const period = pickDefaultPeriod(periods);

  const filteredCheques = useMemo(() => {
    const term = normalizeDigits(searchQuery.trim().toLowerCase());
    return cheques.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!term) return true;
      return [c.sayadId, c.bankName, c.drawerName ?? '', c.payeeName ?? '', c.serialNumber].some(
        (v) => v.toLowerCase().includes(term),
      );
    });
  }, [cheques, statusFilter, searchQuery]);

  const stats = useMemo(() => {
    const acc = (s: ChequeStatus) => {
      const rows = cheques.filter((c) => c.status === s);
      return {
        count: rows.length,
        total: rows.reduce((sum, c) => sum + BigInt(c.amountRial || '0'), 0n).toString(),
      };
    };
    return {
      safe: acc('in_safe'),
      collection: acc('in_collection'),
      cleared: acc('cleared'),
      bounced: acc('bounced'),
    };
  }, [cheques]);

  const openModal = (kind: 'inward' | 'outward' | 'chequebook') => {
    setFormError(null);
    setNotice(null);
    idemKey.current = crypto.randomUUID();
    if (kind === 'inward') {
      setAiFilled({});
      setScanImage('');
      setInward(inwardInitial());
    }
    if (kind === 'outward') setOutward(outwardInitial());
    if (kind === 'chequebook') setChequebook(chequebookInitial());
    setModal(kind);
  };

  const handleAiScanExtracted = (res: ChequeScanResult, previewBase64: string) => {
    const d = res.data;
    const next = inwardInitial();
    const filled: Record<string, boolean> = {};
    const set = (key: string, value: string | null | undefined) => {
      if (value) {
        next[key] = value;
        filled[key] = true;
      }
    };
    set('sayadId', d.sayadId);
    set('serialNumber', d.serialNumber);
    set('series', d.series);
    set('bankName', d.bankName);
    set('branchName', d.branchName);
    set('branchCode', d.branchCode);
    set('accountNumber', d.accountNumber);
    set('iban', d.iban);
    set('amountRial', d.amountRial);
    set('amountWords', d.amountWords);
    set('drawerName', d.drawerName);
    set('drawerNationalId', d.drawerNationalId);
    set('payeeName', d.payeeName);
    set('payeeNationalId', d.payeeNationalId);
    if (d.dueDate && isBusinessDate(d.dueDate)) set('dueDate', d.dueDate);
    setInward(next);
    setAiFilled(filled);
    setScanImage(previewBase64);
    setFormError(null);
    setNotice('اطلاعات از تصویر چک استخراج شد؛ پیش از ثبت آن را بررسی کنید.');
    idemKey.current = crypto.randomUUID();
    setModal('inward');
  };

  const submit = async (e: React.FormEvent, run: () => Promise<void>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setFormError(null);
    setIsSubmitting(true);
    try {
      await run();
      setModal(null);
      setTransition(null);
      setRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      setFormError(
        err instanceof ApiError || !(err instanceof Error) ? bannerMessageFrom(err) : err.message,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const requirePeriod = (): FiscalPeriod => {
    if (!period)
      throw new Error('دورهٔ مالی تعریف نشده است. ابتدا سال و دورهٔ مالی را ایجاد کنید.');
    return period;
  };
  const headers = () => ({ 'idempotency-key': idemKey.current });

  const submitInward = (e: React.FormEvent) =>
    submit(e, async () => {
      const f: Form = { ...inward, sayadId: normalizeDigits(inward.sayadId ?? '') };
      if (!isValidSayadId(f.sayadId ?? '')) throw new Error('شناسه صیاد باید ۱۶ رقم باشد.');
      const amountRial = normalizeDigits(f.amountRial ?? '');
      if (!isPositiveRial(amountRial)) throw new Error('مبلغ باید عدد صحیح مثبت (ریال) باشد.');
      const p = requirePeriod();
      await apiPost(
        `${apiBase}/inward`,
        {
          ...f,
          amountRial,
          fiscalYearId: p.fiscalYearId,
          fiscalPeriodId: p.fiscalPeriodId,
          ...(scanImage ? { imageUrl: scanImage } : {}),
          ...(Object.keys(aiFilled).length ? { aiScanMetadata: { filledFields: aiFilled } } : {}),
        },
        { headers: headers() },
      );
    });

  const submitOutward = (e: React.FormEvent) =>
    submit(e, async () => {
      const f: Form = { ...outward, sayadId: normalizeDigits(outward.sayadId ?? '') };
      if (!isValidSayadId(f.sayadId ?? '')) throw new Error('شناسه صیاد باید ۱۶ رقم باشد.');
      const amountRial = normalizeDigits(f.amountRial ?? '');
      if (!isPositiveRial(amountRial)) throw new Error('مبلغ باید عدد صحیح مثبت (ریال) باشد.');
      const bank = bankAccounts.find((b) => b.bankAccountId === f.bankAccountId);
      const p = requirePeriod();
      await apiPost(
        `${apiBase}/outward`,
        {
          ...f,
          amountRial,
          bankName: f.bankName || bank?.bankName || '',
          ...(f.chequebookId ? {} : { chequebookId: undefined }),
          fiscalYearId: p.fiscalYearId,
          fiscalPeriodId: p.fiscalPeriodId,
        },
        { headers: headers() },
      );
    });

  const submitChequebook = (e: React.FormEvent) =>
    submit(e, async () => {
      const from = normalizeDigits(chequebook.fromSerial ?? '');
      const to = normalizeDigits(chequebook.toSerial ?? '');
      if (!/^\d+$/.test(from) || !/^\d+$/.test(to) || BigInt(to) < BigInt(from)) {
        throw new Error('بازهٔ سریال نامعتبر است (شمارهٔ پایان باید بزرگ‌تر یا برابر شروع باشد).');
      }
      await apiPost(
        `${apiBase}/chequebooks/create`,
        { ...chequebook, fromSerial: from, toSerial: to },
        { headers: headers() },
      );
    });

  const submitTransition = (e: React.FormEvent) =>
    submit(e, async () => {
      if (!transition) return;
      const needsGl = transitionNeedsGlAccount(transition.cheque.direction, transition.to);
      if (needsGl && !transitionForm.targetGlAccountId) {
        throw new Error('حساب دفتر کل مقابل را انتخاب کنید.');
      }
      const p = requirePeriod();
      await apiPost(
        `${apiBase}/${transition.cheque.chequeId}/transition`,
        {
          toStatus: transition.to,
          transitionDate: transitionForm.transitionDate,
          targetTreasuryAccountId: transitionForm.targetTreasuryAccountId || undefined,
          targetGlAccountId: transitionForm.targetGlAccountId || undefined,
          fiscalYearId: p.fiscalYearId,
          fiscalPeriodId: p.fiscalPeriodId,
          notes: transitionForm.notes || undefined,
        },
        { headers: headers() },
      );
    });

  const bind = (state: Form, setState: (f: Form) => void) => (key: string) => ({
    id: `chq-${key}`,
    value: state[key] ?? '',
    onChange: (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setState({ ...state, [key]: ev.target.value }),
  });
  const inw = bind(inward, setInward);
  const outw = bind(outward, setOutward);
  const bk = bind(chequebook, setChequebook);
  const tr = bind(transitionForm, setTransitionForm);

  const glOptions = (
    <>
      <option value="">انتخاب حساب…</option>
      {glAccounts.map((a) => (
        <option key={a.accountId} value={a.accountId}>
          {a.code} · {a.name}
        </option>
      ))}
    </>
  );
  const treasuryOptions = (
    <>
      <option value="">انتخاب…</option>
      {treasuryAccounts.map((a) => (
        <option key={a.treasuryAccountId} value={a.treasuryAccountId}>
          {a.name} ({a.code})
        </option>
      ))}
    </>
  );

  const footer = (label: string, onCancel: () => void) => (
    <div className="chq-dialog-foot">
      <V2Button type="button" variant="secondary" onClick={onCancel}>
        انصراف
      </V2Button>
      <V2Button type="submit" variant="primary" disabled={isSubmitting}>
        {isSubmitting ? 'در حال ثبت…' : label}
      </V2Button>
    </div>
  );

  const kpi = (label: string, s: { count: number; total: string }, color: string) => (
    <div className="chq-kpi">
      <div className="chq-kpi-label">{label}</div>
      <div className="chq-kpi-value" style={{ color }}>
        {formatRialFa(s.total, unit)}
      </div>
      <div className="chq-kpi-count">{s.count} فقره چک</div>
    </div>
  );

  const statusOptions =
    activeTab === 'outward'
      ? (['issued', 'delivered', 'cleared', 'bounced', 'voided'] as const)
      : (['in_safe', 'in_collection', 'cleared', 'bounced', 'returned'] as const);

  return (
    <div className="chq-page" data-testid="cheques-page">
      <div className="chq-bar">
        <span className="chq-hint">
          ثبت هوشمند با دوربین، استعلام صیاد، چرخهٔ وصول و اسناد حسابداری خودکار
        </span>
        <div className="chq-actions">
          <V2Button variant="primary" onClick={() => setIsScannerOpen(true)}>
            اسکن چک با دوربین
          </V2Button>
          <V2Button variant="secondary" onClick={() => openModal('inward')}>
            ثبت چک دریافتی
          </V2Button>
          <V2Button variant="secondary" onClick={() => openModal('outward')}>
            صدور چک پرداختی
          </V2Button>
          <V2Button variant="secondary" onClick={() => openModal('chequebook')}>
            تعریف دسته‌چک
          </V2Button>
        </div>
      </div>

      {notice && !modal && (
        <div role="status" className="chq-hint">
          {notice}
        </div>
      )}

      {activeTab !== 'chequebooks' && (
        <div className="chq-kpis">
          {kpi('نزد صندوق', stats.safe, 'var(--sk-color-info)')}
          {kpi('در جریان وصول', stats.collection, 'var(--sk-color-warn)')}
          {kpi('وصول‌شده', stats.cleared, 'var(--sk-color-success)')}
          {kpi('برگشتی', stats.bounced, 'var(--sk-color-danger)')}
        </div>
      )}

      <div className="chq-tabs" role="tablist">
        {(
          [
            ['inward', 'چک‌های دریافتی'],
            ['outward', 'چک‌های پرداختی'],
            ['chequebooks', 'دسته‌چک‌ها'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            className="chq-tab"
            aria-selected={activeTab === id}
            onClick={() => {
              setActiveTab(id);
              setStatusFilter('all');
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab !== 'chequebooks' && (
        <div className="chq-filters">
          <input
            type="search"
            aria-label="جست‌وجو"
            placeholder="جست‌وجو با شناسه صیاد، بانک، طرف حساب، سریال…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            aria-label="فیلتر وضعیت"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">همه وضعیت‌ها</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {CHEQUE_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </div>
      )}

      {activeTab !== 'chequebooks' ? (
        chequesQuery.status === 'loading' ? (
          <LoadingState />
        ) : chequesQuery.status === 'error' ? (
          <StatePanel
            variant={chequesQuery.error.status === 403 ? 'permission_denied' : 'network_error'}
            title={
              chequesQuery.error.status === 403
                ? 'دسترسی به بخش چک ندارید'
                : 'چک‌ها قابل نمایش نیستند'
            }
          />
        ) : filteredCheques.length === 0 ? (
          <V2EmptyState
            icon="invoice"
            title="چکی یافت نشد"
            description="هیچ چکی با این مشخصات یا در این وضعیت یافت نشد."
          />
        ) : (
          <div className="chq-table-wrap">
            <table className="chq-table" data-testid="cheques-table">
              <thead>
                <tr>
                  <th>شناسه صیاد</th>
                  <th>سریال</th>
                  <th>سررسید</th>
                  <th>مبلغ</th>
                  <th>بانک</th>
                  <th>طرف چک</th>
                  <th>وضعیت</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {filteredCheques.map((c) => (
                  <tr key={c.chequeId}>
                    <td data-label="شناسه صیاد" className="chq-mono">
                      {c.sayadId}
                    </td>
                    <td data-label="سریال">
                      {c.serialNumber} {c.series ? `(${c.series})` : ''}
                    </td>
                    <td data-label="سررسید">{displayJalaliDate(c.dueDate)}</td>
                    <td data-label="مبلغ" style={{ fontWeight: 700 }}>
                      {formatRialFa(c.amountRial, unit)}
                    </td>
                    <td data-label="بانک">
                      {c.bankName} {c.branchName ? `· ${c.branchName}` : ''}
                    </td>
                    <td data-label="طرف چک">
                      {c.direction === 'inward'
                        ? `از: ${c.drawerName || 'مشتری'}`
                        : `در وجه: ${c.payeeName || 'ذینفع'}`}
                    </td>
                    <td data-label="وضعیت">
                      <span className="chq-badge" data-status={c.status}>
                        {CHEQUE_STATUS_LABEL[c.status] ?? c.status}
                      </span>
                    </td>
                    <td data-label="عملیات">
                      <div className="chq-row-actions">
                        {allowedChequeTransitions(c.direction, c.status).map((to) => (
                          <V2Button
                            key={to}
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setFormError(null);
                              idemKey.current = crypto.randomUUID();
                              setTransitionForm(transitionInitial());
                              setTransition({ cheque: c, to });
                            }}
                          >
                            {TRANSITION_BUTTON_LABEL[to] ?? CHEQUE_STATUS_LABEL[to]}
                          </V2Button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : chequebooksQuery.status === 'loading' ? (
        <LoadingState />
      ) : chequebooksQuery.status === 'error' ? (
        <StatePanel variant="network_error" title="دسته‌چک‌ها قابل نمایش نیستند" />
      ) : chequebooks.length === 0 ? (
        <V2EmptyState
          icon="invoice"
          title="دسته‌چکی ثبت نشده"
          description="با «تعریف دسته‌چک» اولین دسته‌چک را ثبت کنید."
        />
      ) : (
        <div className="chq-table-wrap">
          <table className="chq-table" data-testid="chequebooks-table">
            <thead>
              <tr>
                <th>سری</th>
                <th>شروع</th>
                <th>پایان</th>
                <th>برگ</th>
                <th>استفاده‌شده</th>
                <th>باقیمانده</th>
                <th>وضعیت</th>
              </tr>
            </thead>
            <tbody>
              {chequebooks.map((b) => (
                <tr key={b.chequebookId}>
                  <td data-label="سری" style={{ fontWeight: 600 }}>
                    {b.series}
                  </td>
                  <td data-label="شروع" className="chq-mono">
                    {b.fromSerial}
                  </td>
                  <td data-label="پایان" className="chq-mono">
                    {b.toSerial}
                  </td>
                  <td data-label="برگ">{b.leafCount}</td>
                  <td data-label="استفاده‌شده">{b.usedCount}</td>
                  <td data-label="باقیمانده" style={{ fontWeight: 700 }}>
                    {b.leafCount - b.usedCount - b.voidedCount}
                  </td>
                  <td data-label="وضعیت">{b.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ChequeCameraScanner
        isOpen={isScannerOpen}
        scanUrl={`${apiBase}/scan-ai`}
        onClose={() => setIsScannerOpen(false)}
        onExtracted={handleAiScanExtracted}
      />

      {modal === 'inward' && (
        <ChequeModal
          title={`ثبت چک دریافتی${Object.keys(aiFilled).length ? ' ✨ استخراج‌شده از تصویر' : ''}`}
          onClose={() => setModal(null)}
          testId="inward-modal"
        >
          {notice && (
            <div role="status" className="chq-hint">
              {notice}
            </div>
          )}
          {formError && (
            <div role="alert" className="chq-error">
              {formError}
            </div>
          )}
          <form onSubmit={submitInward}>
            <div className="chq-grid">
              <ChequeField
                id="chq-sayadId"
                label="شناسه ۱۶ رقمی صیاد"
                required
                ai={aiFilled.sayadId}
              >
                <input
                  {...inw('sayadId')}
                  required
                  inputMode="numeric"
                  maxLength={16}
                  data-ai={!!aiFilled.sayadId}
                  className="chq-mono"
                />
              </ChequeField>
              <ChequeField
                id="chq-serialNumber"
                label="شمارهٔ سریال"
                required
                ai={aiFilled.serialNumber}
              >
                <input {...inw('serialNumber')} required data-ai={!!aiFilled.serialNumber} />
              </ChequeField>
              <ChequeField
                id="chq-amountRial"
                label="مبلغ (ریال)"
                required
                ai={aiFilled.amountRial}
              >
                <input
                  {...inw('amountRial')}
                  required
                  inputMode="numeric"
                  data-ai={!!aiFilled.amountRial}
                />
              </ChequeField>
              <ChequeField id="chq-dueDate" label="تاریخ سررسید" required ai={aiFilled.dueDate}>
                <JalaliDateInput
                  value={inward.dueDate as BusinessDate}
                  onChange={(d) => setInward({ ...inward, dueDate: d })}
                />
              </ChequeField>
              <ChequeField id="chq-bankName" label="نام بانک" required ai={aiFilled.bankName}>
                <input {...inw('bankName')} required data-ai={!!aiFilled.bankName} />
              </ChequeField>
              <ChequeField id="chq-branchName" label="شعبه" ai={aiFilled.branchName}>
                <input {...inw('branchName')} />
              </ChequeField>
              <ChequeField id="chq-drawerName" label="صادرکننده" ai={aiFilled.drawerName}>
                <input {...inw('drawerName')} data-ai={!!aiFilled.drawerName} />
              </ChequeField>
              <ChequeField id="chq-iban" label="شمارهٔ شبا">
                <input {...inw('iban')} />
              </ChequeField>
              <ChequeField id="chq-treasuryAccountId" label="صندوق نگه‌دارندهٔ چک" required>
                <select {...inw('treasuryAccountId')} required>
                  {treasuryOptions}
                </select>
              </ChequeField>
              <ChequeField id="chq-chequeAccountId" label="حساب اسناد دریافتنی" required>
                <select {...inw('chequeAccountId')} required>
                  {glOptions}
                </select>
              </ChequeField>
              <ChequeField id="chq-counterpartAccountId" label="حساب مقابل (مشتری/درآمد)" required>
                <select {...inw('counterpartAccountId')} required>
                  {glOptions}
                </select>
              </ChequeField>
            </div>
            {footer('ثبت چک و صدور سند', () => setModal(null))}
          </form>
        </ChequeModal>
      )}

      {modal === 'outward' && (
        <ChequeModal title="صدور چک پرداختی" onClose={() => setModal(null)} testId="outward-modal">
          {formError && (
            <div role="alert" className="chq-error">
              {formError}
            </div>
          )}
          <form onSubmit={submitOutward}>
            <div className="chq-grid">
              <ChequeField id="chq-bankAccountId" label="حساب بانکی" required>
                <select {...outw('bankAccountId')} required>
                  <option value="">انتخاب…</option>
                  {bankAccounts.map((b) => (
                    <option key={b.bankAccountId} value={b.bankAccountId}>
                      {b.name} ({b.code})
                    </option>
                  ))}
                </select>
              </ChequeField>
              <ChequeField id="chq-chequebookId" label="دسته‌چک">
                <select {...outw('chequebookId')}>
                  <option value="">بدون دسته‌چک</option>
                  {chequebooks
                    .filter(
                      (b) => !outward.bankAccountId || b.bankAccountId === outward.bankAccountId,
                    )
                    .map((b) => (
                      <option key={b.chequebookId} value={b.chequebookId}>
                        {b.series} ({b.fromSerial}–{b.toSerial})
                      </option>
                    ))}
                </select>
              </ChequeField>
              <ChequeField id="chq-sayadId" label="شناسه ۱۶ رقمی صیاد" required>
                <input
                  {...outw('sayadId')}
                  required
                  inputMode="numeric"
                  maxLength={16}
                  className="chq-mono"
                />
              </ChequeField>
              <ChequeField id="chq-serialNumber" label="شمارهٔ سریال" required>
                <input {...outw('serialNumber')} required />
              </ChequeField>
              <ChequeField id="chq-amountRial" label="مبلغ (ریال)" required>
                <input {...outw('amountRial')} required inputMode="numeric" />
              </ChequeField>
              <ChequeField id="chq-dueDate" label="تاریخ سررسید" required>
                <JalaliDateInput
                  value={outward.dueDate as BusinessDate}
                  onChange={(d) => setOutward({ ...outward, dueDate: d })}
                />
              </ChequeField>
              <ChequeField id="chq-payeeName" label="در وجه" required>
                <input {...outw('payeeName')} required />
              </ChequeField>
              <ChequeField id="chq-payeeNationalId" label="کد/شناسهٔ ملی ذینفع">
                <input {...outw('payeeNationalId')} inputMode="numeric" />
              </ChequeField>
              <ChequeField id="chq-chequePayableAccountId" label="حساب اسناد پرداختنی" required>
                <select {...outw('chequePayableAccountId')} required>
                  {glOptions}
                </select>
              </ChequeField>
              <ChequeField
                id="chq-counterpartAccountId"
                label="حساب مقابل (تأمین‌کننده/هزینه)"
                required
              >
                <select {...outw('counterpartAccountId')} required>
                  {glOptions}
                </select>
              </ChequeField>
            </div>
            {footer('صدور چک و ثبت سند', () => setModal(null))}
          </form>
        </ChequeModal>
      )}

      {modal === 'chequebook' && (
        <ChequeModal
          title="تعریف دسته‌چک"
          onClose={() => setModal(null)}
          maxWidth={520}
          testId="chequebook-modal"
        >
          {formError && (
            <div role="alert" className="chq-error">
              {formError}
            </div>
          )}
          <form onSubmit={submitChequebook}>
            <div className="chq-grid">
              <ChequeField id="chq-bankAccountId" label="حساب بانکی" required>
                <select {...bk('bankAccountId')} required>
                  <option value="">انتخاب…</option>
                  {bankAccounts.map((b) => (
                    <option key={b.bankAccountId} value={b.bankAccountId}>
                      {b.name} ({b.code})
                    </option>
                  ))}
                </select>
              </ChequeField>
              <ChequeField id="chq-series" label="سری دسته‌چک" required>
                <input {...bk('series')} required />
              </ChequeField>
              <ChequeField id="chq-fromSerial" label="شمارهٔ شروع" required>
                <input {...bk('fromSerial')} required inputMode="numeric" />
              </ChequeField>
              <ChequeField id="chq-toSerial" label="شمارهٔ پایان" required>
                <input {...bk('toSerial')} required inputMode="numeric" />
              </ChequeField>
            </div>
            {footer('ثبت دسته‌چک', () => setModal(null))}
          </form>
        </ChequeModal>
      )}

      {transition && (
        <ChequeModal
          title={`تغییر وضعیت چک به «${CHEQUE_STATUS_LABEL[transition.to]}»`}
          onClose={() => setTransition(null)}
          maxWidth={520}
          testId="transition-modal"
        >
          <p className="chq-hint" style={{ margin: '0 0 12px' }}>
            چک صیادی {transition.cheque.sayadId} به مبلغ{' '}
            {formatRialFa(transition.cheque.amountRial, unit)}
          </p>
          {formError && (
            <div role="alert" className="chq-error">
              {formError}
            </div>
          )}
          <form onSubmit={submitTransition}>
            <div className="chq-grid" style={{ gridTemplateColumns: '1fr' }}>
              <ChequeField id="chq-transitionDate" label="تاریخ اقدام" required>
                <JalaliDateInput
                  value={transitionForm.transitionDate as BusinessDate}
                  onChange={(d) => setTransitionForm({ ...transitionForm, transitionDate: d })}
                />
              </ChequeField>
              {transitionNeedsGlAccount(transition.cheque.direction, transition.to) && (
                <ChequeField id="chq-targetGlAccountId" label="حساب دفتر کل مقابل" required>
                  <select {...tr('targetGlAccountId')} required>
                    {glOptions}
                  </select>
                </ChequeField>
              )}
              {transition.cheque.direction === 'inward' && (
                <ChequeField id="chq-targetTreasuryAccountId" label="حساب بانکی / خزانهٔ مقصد">
                  <select {...tr('targetTreasuryAccountId')}>{treasuryOptions}</select>
                </ChequeField>
              )}
              <ChequeField id="chq-notes" label="توضیحات">
                <textarea {...tr('notes')} rows={2} />
              </ChequeField>
            </div>
            {footer('تأیید و ثبت', () => setTransition(null))}
          </form>
        </ChequeModal>
      )}
    </div>
  );
}
